/**
 * Whether a 2D context reports itself lost. Safari (desktop and iOS) has no
 * `isContextLost()`; calling it there throws on the very first paint, which the
 * renderer reads as a draw failure and tears the session down.
 */
export function contextLost(context: { isContextLost?: () => boolean }): boolean {
  return typeof context.isContextLost === 'function' && context.isContextLost();
}

/**
 * Completes a draw whose source is about to be closed. A one-pixel readback forces the 2D
 * context to resolve pending drawing before `VideoFrame.close()` releases the decoder output.
 */
export function flushDraw(context: { getImageData?: (x: number, y: number, w: number, h: number) => unknown }): void {
  if (typeof context.getImageData !== 'function') return;
  try { context.getImageData(0, 0, 1, 1); } catch { /* A tainted or lost context is reported by the next draw. */ }
}

/** Resize without wiping a picture that is already on the canvas. */
export function resizeKeepingPicture(
  canvas: { width: number; height: number; transferToImageBitmap(): { close(): void } },
  width: number,
  height: number,
  draw: (image: { close(): void }) => void,
): void {
  if (canvas.width === width && canvas.height === height) return;
  const snapshot = canvas.width > 0 && canvas.height > 0 ? canvas.transferToImageBitmap() : null;
  canvas.width = width;
  canvas.height = height;
  if (!snapshot) return;
  draw(snapshot);
  snapshot.close();
}
